import { useCallback } from 'react';
import { RewardType } from '~/shared';
import { RewardFormData } from '../redux/slices/promoSlice';
import { useDateValidation } from './useDateValidation';
import { useNumberFormat } from './useNumberFormat';

interface ValidationError {
	field: string;
	message: string;
}

interface RewardFieldConfig {
	label: string;
	placeholder: string;
	type: 'text' | 'number';
	required?: boolean;
}

const getRewardFieldsConfig = (rewardType: RewardType): RewardFieldConfig[] => {
	switch (rewardType) {
		case RewardType.MONEY:
			return [
				{
					label: 'Сумма',
					placeholder: '1000',
					type: 'number',
					required: true,
				},
			];
		case RewardType.EXP:
			return [
				{
					label: 'Количество опыта',
					placeholder: '100',
					type: 'number',
					required: true,
				},
			];
		case RewardType.ITEM:
			return [
				{
					label: 'ID предмета',
					placeholder: 'item_id',
					type: 'text',
					required: true,
				},
				{
					label: 'Количество',
					placeholder: '1',
					type: 'number',
					required: false,
				},
				{
					label: 'Данные (JSON)',
					placeholder: '{"breakable":{"durability":100}}',
					type: 'text',
					required: false,
				},
			];
		case RewardType.VEHICLE:
			return [
				{
					label: 'Модель ТС',
					placeholder: 'bmw_m5f90',
					type: 'text',
					required: true,
				},
				{
					label: 'Тюнинг (ID модификаций)',
					placeholder: '0,1,2,3',
					type: 'text',
					required: false,
				},
				{
					label: 'Набор тюнинга',
					placeholder: 'kit_name',
					type: 'text',
					required: false,
				},
			];
		case RewardType.LOOTBOX:
			return [
				{
					label: 'ID кейса',
					placeholder: 'case_rare',
					type: 'text',
					required: true,
				},
			];
		case RewardType.BUNDLE:
			return [
				{
					label: 'ID набора',
					placeholder: 'bundle_start',
					type: 'text',
					required: true,
				},
			];
		default:
			return [];
	}
};

export const usePromoValidation = () => {
	const { parseFormattedNumber } = useNumberFormat();
	const { isValidDateString, parseDateString } = useDateValidation();

	const getValidRewards = useCallback(
		(rewards: RewardFormData[]): RewardFormData[] => {
			return rewards.filter((r) => {
				if (!r.value) return false;
				if (
					(r.type === RewardType.MONEY || r.type === RewardType.EXP) &&
					parseInt(r.value) === 0
				)
					return false;
				return true;
			});
		},
		[],
	);

	const getRewardValueString = useCallback((reward: RewardFormData): string => {
		// Only ITEM and VEHICLE types use JSON format in value
		// MONEY, EXP, LOOTBOX, BUNDLE store plain strings
		switch (reward.type) {
			case RewardType.MONEY:
			case RewardType.EXP:
			case RewardType.LOOTBOX:
			case RewardType.BUNDLE:
				return reward.value;
			case RewardType.ITEM: {
				let parsed: Record<string, unknown> = {};
				try {
					parsed = JSON.parse(reward.value || '{}');
				} catch {
					// If not JSON, treat value as id
					parsed = { id: reward.value };
				}
				return JSON.stringify({
					id: parsed.id || reward.value || '',
					count: parsed.count || 1,
					data: parsed.data || undefined,
				});
			}
			case RewardType.VEHICLE: {
				let parsed: Record<string, unknown> = {};
				try {
					parsed = JSON.parse(reward.value || '{}');
				} catch {
					// If not JSON, treat value as model
					parsed = { model: reward.value };
				}
				return JSON.stringify({
					model: parsed.model || reward.value || '',
					tuning: parsed.tuning || undefined,
				});
			}
			default:
				return reward.value;
		}
	}, []);

	const validateForm = useCallback(
		(formData: {
			code: string;
			dateStart: string;
			dateEnd: string;
			requiredPlayTimeMin: number;
			maxActivations: string;
			rewards: RewardFormData[];
		}): ValidationError[] => {
			const errors: ValidationError[] = [];

			if (!formData.code.trim()) {
				errors.push({ field: 'code', message: 'Код промокода обязателен' });
			}

			if (formData.dateEnd && !isValidDateString(formData.dateEnd)) {
				errors.push({ field: 'dateEnd', message: 'Неверный формат даты' });
			}

			if (formData.dateStart && !isValidDateString(formData.dateStart)) {
				errors.push({ field: 'dateStart', message: 'Неверный формат даты' });
			}

			const parsedPlayTime = parseFormattedNumber(
				formData.requiredPlayTimeMin.toString(),
			);
			if (!parsedPlayTime || parseInt(parsedPlayTime) === 0) {
				errors.push({
					field: 'requiredPlayTimeMin',
					message: 'Время игры должно быть больше 0',
				});
			}

			const parsedMaxActivations = parseFormattedNumber(
				formData.maxActivations,
			);
			if (
				formData.maxActivations &&
				parsedMaxActivations &&
				parseInt(parsedMaxActivations) === 0
			) {
				errors.push({
					field: 'maxActivations',
					message: 'Макс. активаций должно быть больше 0',
				});
			}

			const validRewards = getValidRewards(formData.rewards);

			if (validRewards.length === 0) {
				errors.push({
					field: 'rewards',
					message: 'Добавьте хотя бы одну награду',
				});
			}

			validRewards.forEach((reward, index) => {
				const fields = getRewardFieldsConfig(reward.type);
				fields.forEach((field, fieldIndex) => {
					if (field.required && !reward.value) {
						errors.push({
							field: `reward_${index}_${fieldIndex}`,
							message: `Награда ${index + 1}: ${field.label} обязательно`,
						});
					}
				});
			});

			return errors;
		},
		[isValidDateString, parseFormattedNumber, getValidRewards],
	);

	return {
		getValidRewards,
		getRewardValueString,
		validateForm,
		parseDateString,
	};
};
